/**
 * Google Arts & Culture
 *
 * Declares a single web-accessible-resource entry (`*.sourcemap` and three named
 * `fonts/*.woff2` files) matched to every origin with no `use_dynamic_url` — so its
 * per-surface source maps and fonts are fetchable from any page at a fixed URL.
 *
 * There are no bundled icons in the WAR set, so the primary signal is a vendor-named
 * source map (`js_background.nodeps.sourcemap`); a second source map and the icon font
 * corroborate it.
 *
 * Manifest verified against version 3.1.0.
 */

import { probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'akimgimeeoiognljlfchpbkpfbmeapkh';

export const googleArtsAndCulture: ExtensionDefinition = {
  id: ID,
  name: 'Google Arts & Culture',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 200_000,
  notes:
    'Exposes its source maps and three named fonts to every origin with no dynamic URL. ' +
    'The vendor-named nodeps source maps are conclusive.',
  signals: [
    {
      id: 'war-background-sourcemap',
      kind: 'web-accessible-resource',
      description:
        'Fetch background/js_background.nodeps.sourcemap — a vendor-named source map ' +
        'exposed to every origin at a stable path.',
      weight: 1,
      run: () => probeFetch(ID, 'background/js_background.nodeps.sourcemap'),
    },
    {
      id: 'war-newtab-sourcemap',
      kind: 'web-accessible-resource',
      description:
        'Fetch newtab/js_newtab.nodeps.sourcemap — the new-tab surface source map, a ' +
        'second distinctive path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'newtab/js_newtab.nodeps.sourcemap'),
    },
    {
      id: 'war-mat-icons-woff2',
      kind: 'web-accessible-resource',
      description:
        'Fetch fonts/mat_icons.woff2 — a named icon font in the WAR set, independent of ' +
        'the source-map paths.',
      weight: 0.5,
      run: () => probeFetch(ID, 'fonts/mat_icons.woff2'),
    },
  ],
};
